import React from 'react';
import { Formik } from 'formik';
import { Col, DatePicker, Row, Tooltip } from 'antd';
import { Form, FormItem, Input, ResetButton, SubmitButton, Select, Radio } from 'formik-antd';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import moment from 'moment';
import { InfoCircleOutlined } from '@ant-design/icons';
import isEmpty from 'lodash/isEmpty';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import Yup from '../../../../vendor/yup';
import { useGetTemplatesQuery } from '../../../Template builder/api/templatesApiSlice';
import { PrivateComponent } from '../../../../components/HasRights/HasRights';
import { Permissions } from '../../../../const/permissions';
import useDatepickerHandler from '../../../../app/hooks/useDatepickerHandler';
import makeTemplatesQueryParams from '../../../../lib/makeTemplatesQueryParams';
import makeSelectFilterOption from '../../../../lib/makeSelectFilterOption';
import mkTemplatesOptions from '../../utils/mkTemplatesOptions';
import { DATE_FORMAT } from '../../../../const/system';
import { COLLECTION_TEMPLATE_CONTENT_TYPE } from '../../pages/Collections/PageCollections/PageCollections.const';



const validationSchema = Yup.lazy((values) => Yup.object().shape({
  id: Yup.string(),
  title: Yup.string().max(255, 'Maximum 255 symbols required'),
  categories: Yup.array(),
  image_type: Yup.string(),
  template: Yup.string(),
  released_at: Yup.string().test(
    'test_date_after',
    'Released at date must be same or before than released to',
    (value) => {
      if (!isEmpty(values?.released_to) && !isEmpty(value)) {
        return moment(values?.released_to).isSameOrAfter(value);
      }
      return true;
    },
  ),
  released_to: Yup.string().test(
    'test_date_before',
    'Released to date must be same or after than released at',
    (value) => {
      if (!isEmpty(values?.released_at) && !isEmpty(value)) {
        return moment(values?.released_at).isSameOrBefore(value);
      }
      return true;
    },
  ),
}));

const FormCollectionsFilter = ({
  initialValues,
  formErrors = {},
  onSubmit,
  onCancel,
  isSubmitting,
  imageTypeOptions,
  categoriesOptions,
}) => {
  const intl = useIntl();

  const { data: { data: templates } = { data: [] }, isFetching: isFetchingTemplates } = useGetTemplatesQuery({
    queryParams: makeTemplatesQueryParams('is_used', COLLECTION_TEMPLATE_CONTENT_TYPE),
  });

  const { onBlur } = useDatepickerHandler();

  return (
    <Formik
      enableReinitialize
      isSubmitting
      initialErrors={formErrors}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      onReset={onCancel}
    >
      {({ isValid, dirty, values, setFieldValue }) => {
        return (
          <Form layout="vertical" >
            <FormItem
              name='id'
              label={
                <Row className='w-full' justify="space-between" align="middle">
                  <IntlMessages id="ui-general-id" />
                  <Tooltip placement="topRight" title={<IntlMessages id="ui-general-filter-id-key-help" />}>
                    <InfoCircleOutlined className="hp-ml-8" />
                  </Tooltip>
                </Row>
              }
            >
              <Input
                name="id"
                placeholder={intl.formatMessage({ id: 'ui-general-ids-placeholder' })}
              />
            </FormItem>

            <FormItem
              label={intl.formatMessage({ id: 'ui-general-title' })}
              name='title'
            >
              <Input
                name="title"
                placeholder={intl.formatMessage({ id: 'ui-general-specify-title' })}
              />
            </FormItem>

            <FormItem
              label={intl.formatMessage({ id: 'collection-form-categories-title' })}
              name='categories'
            >
              <Select
                name="categories"
                mode="multiple"
                allowClear
                showSearch
                options={categoriesOptions}
                loading={isSubmitting}
                placeholder={intl.formatMessage({ id: 'ui-general-choose-categories' })}
                filterOption={(input, option) => makeSelectFilterOption(option.label, input)}
              />
            </FormItem>

            <FormItem
              label={intl.formatMessage({ id: 'collection-form-image-type-title' })}
              name='image_type'
            >
              <Select
                allowClear
                name="image_type"
                options={imageTypeOptions}
                placeholder={intl.formatMessage({ id: 'collection-form-specify-image-type' })}
              />
            </FormItem>

            <PrivateComponent allowedPermissions={[ Permissions.CONSTRUCTORS.TEMPLATES.FILTER ]}>
              <FormItem
                label={intl.formatMessage({ id: 'ui-general-template' })}
                name='template'
              >
                <Select
                  allowClear
                  showSearch
                  loading={isFetchingTemplates}
                  name="template"
                  value={values?.template?.toString() ?? null}
                  options={mkTemplatesOptions(templates)}
                  placeholder={intl.formatMessage({ id: 'ui-general-choose-template' })}
                  filterOption={(input, option) => makeSelectFilterOption(option.label, input)}
                />
              </FormItem>
            </PrivateComponent>

            <Row gutter={16}>
              <Col span={12}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-released-at' })}
                  name='released_at'
                >
                  <DatePicker
                    name='released_at'
                    format={DATE_FORMAT}
                    showToday
                    value={values?.released_at ? moment(values.released_at) : null}
                    onChange={async (date, dateString) => {
                      await setFieldValue('released_at', dateString);
                    }}
                    onBlur={async ({ target }) => {
                      await onBlur('released_at', target.value, setFieldValue);
                    }}
                  />
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-released-to' })}
                  name='released_to'
                >
                  <DatePicker
                    name='released_to'
                    format={DATE_FORMAT}
                    value={values?.released_to ? moment(values.released_to) : null}
                    onChange={async (date, dateString) => {
                      await setFieldValue('released_to', dateString);
                    }}
                    onBlur={async ({ target }) => {
                      await onBlur('released_to', target.value, setFieldValue);
                    }}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[ 16, 16 ]} justify='start'>
              <Col span={12}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-published' })}
                  name='is_published'
                >
                  <Radio.Group
                    size='small'
                    name='is_published'
                    defaultValue={null}
                  >
                    <Radio.Button value={null}>{intl.formatMessage({ id: 'ui-general-all' })}</Radio.Button>
                    <Radio.Button value={1}>{intl.formatMessage({ id: 'ui-general-yes' })}</Radio.Button>
                    <Radio.Button value={0}>{intl.formatMessage({ id: 'ui-general-no' })}</Radio.Button>
                  </Radio.Group>
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[ 16, 16 ]} justify='end'>
              <Col>
                <ResetButton disabled={false}>
                  <IntlMessages id='ui-general-reset' />
                </ResetButton>
              </Col>

              <Col>
                <SubmitButton
                  type="primary"
                  loading={isSubmitting}
                  disabled={!isValid || !dirty}
                >
                  <IntlMessages id="ui-general-apply" />
                </SubmitButton>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};

FormCollectionsFilter.propTypes = {
  initialValues: PropTypes.object.isRequired,
  formErrors: PropTypes.object,
  imageTypeOptions: PropTypes.array.isRequired,
  categoriesOptions: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};


export default FormCollectionsFilter;
